import { type Rational, rat, add, sub, eq, neg, isInteger, key } from '../math/rational.ts';
import type { Rng } from '../math/rng.ts';
import type { LevelDef, Problem, ProblemTemplate } from '../types.ts';
import { validateMarkup } from './markup.ts';
import { ALL_TEMPLATES } from './templates/index.ts';

const REGISTRY = new Map<string, ProblemTemplate>();
for (const t of ALL_TEMPLATES) {
  if (REGISTRY.has(t.id)) throw new Error(`duplicate template id ${t.id}`);
  REGISTRY.set(t.id, t);
}

export function getTemplate(id: string): ProblemTemplate {
  const t = REGISTRY.get(id);
  if (!t) throw new Error(`unknown template ${id}`);
  return t;
}

export function hasTemplate(id: string): boolean {
  return REGISTRY.has(id);
}

export function allTemplates(): ProblemTemplate[] {
  return [...REGISTRY.values()];
}

const defaultParScale = (d: number) => 0.85 + 0.3 * d;

/** Plausible "near miss" values used when a template supplies fewer than 3 distractors. */
function neighbourCandidates(answer: Rational): Rational[] {
  const one = rat(1);
  const out: Rational[] = [add(answer, one), sub(answer, one), add(answer, rat(2)), sub(answer, rat(2)), neg(answer)];
  if (Math.abs(answer.n / answer.d) >= 10) out.push(add(answer, rat(10)), sub(answer, rat(10)));
  if (!isInteger(answer)) out.push(rat(answer.d, answer.n || 1), rat(answer.n + 1, answer.d), rat(answer.n, answer.d + 1));
  else out.push(rat(answer.n * 2), add(answer, rat(3)));
  return out;
}

/**
 * Build the 4 answer choices: the answer + 3 distinct distractors.
 * Template distractors come first (they encode real misconceptions); neighbours fill the gaps.
 */
export function buildChoices(
  rng: Rng,
  answer: Rational,
  distractors: readonly Rational[],
  allowNegative: boolean,
): { choices: [Rational, Rational, Rational, Rational]; correctChoice: 0 | 1 | 2 | 3 } {
  const ok = (v: Rational) => !eq(v, answer) && (allowNegative || v.n >= 0 || answer.n < 0);
  const picked: Rational[] = [];
  const seen = new Set<string>([key(answer)]);
  const consider = (v: Rational) => {
    if (picked.length >= 3 || !ok(v) || seen.has(key(v))) return;
    seen.add(key(v));
    picked.push(v);
  };
  // Shuffle template distractors so the same misconception isn't always in the same slot.
  for (const d of rng.shuffle(distractors)) consider(d);
  for (const d of neighbourCandidates(answer)) consider(d);
  let k = 3;
  while (picked.length < 3) consider(add(answer, rat(k++))); // always terminates
  const all = rng.shuffle([answer, ...picked]);
  const correctChoice = all.findIndex((v) => eq(v, answer)) as 0 | 1 | 2 | 3;
  return { choices: all as [Rational, Rational, Rational, Rational], correctChoice };
}

/** Generate the problem with sequence number `seq` for `level`. Deterministic given rng state. */
export function generateProblem(rng: Rng, level: LevelDef, seq: number): Problem {
  const templateId = rng.weighted(level.templates.map((t) => ({ item: t.id, weight: t.weight })));
  const template = getTemplate(templateId);
  const [lo, hi] = level.templateDifficulty;
  const difficulty = Math.round((lo + (hi - lo) * rng.next()) * 1000) / 1000;
  const core = template.generate(rng, difficulty);
  validateMarkup(core.prompt);
  const { choices, correctChoice } = buildChoices(
    rng,
    core.answer,
    core.distractors,
    template.allowNegative ?? template.grade >= 7,
  );
  const parTimeMs = Math.round(template.parTimeMs * (template.parScale ?? defaultParScale)(difficulty));
  return {
    id: `${seq}`,
    seq,
    templateId,
    grade: template.grade,
    level: level.index,
    difficulty,
    prompt: core.prompt,
    ...(core.instruction ? { instruction: core.instruction } : {}),
    answer: core.answer,
    answerKind: core.answerKind,
    choices,
    correctChoice,
    parTimeMs,
  };
}
